import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNotificationMeta, type Notification } from '../../hooks/useNotifications';
import { useNotificationsContext } from '../../contexts/NotificationsContext';
import { usePinLockContext } from '../../contexts/PinLockContext';

interface AppHeaderProps {
  isCollapsed: boolean;
  onToggleSidebar: () => void;
  onOpenMobileSidebar: () => void;
  onSignOut: () => Promise<void>;
  user: {
    name: string;
    fullName: string;
    avatarInitials: string;
    plan: 'free' | 'paid';
    planName: string;
    avatarUrl?: string | null;
  };
}

const timeAgo = (dateStr: string): string => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'agora mesmo';
  if (mins < 60) return `${mins}m atrás`;
  if (hours < 24) return `${hours}h atrás`;
  return `${days}d atrás`;
};

const fmtDatetime = (dateStr: string): string =>
  new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const fmtRelative = (dateStr: string): string => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'agora mesmo';
  if (mins < 60) return `${mins}m atrás`;
  if (hours < 24) return `${hours}h atrás`;
  if (days === 1) return 'ontem';
  return `${days} dias atrás`;
};

const TYPE_LABEL: Record<string, string> = {
  broadcast: 'Comunicado',
  payment_approved: 'Pagamento',
  payment_rejected: 'Pagamento',
  payment_pending: 'Pagamento',
  upgrade_completed: 'Plano',
  plan_limit_reached: 'Limite',
  goal_completed: 'Meta',
  calendar_pending: 'Pendência',
  calendar_overdue: 'Vencido',
  card_due_reminder: 'Cartão',
  card_expiring: 'Cartão',
  account_expired: 'Conta',
  info: 'Sistema',
  warning: 'Alerta',
  success: 'Sucesso',
};

const TYPE_BADGE_COLOR: Record<string, string> = {
  broadcast: 'bg-violet-50 text-violet-600',
  payment_approved: 'bg-emerald-50 text-emerald-600',
  payment_rejected: 'bg-red-50 text-red-600',
  payment_pending: 'bg-yellow-50 text-yellow-600',
  upgrade_completed: 'bg-amber-50 text-amber-600',
  plan_limit_reached: 'bg-orange-50 text-orange-600',
  goal_completed: 'bg-emerald-50 text-emerald-600',
  calendar_pending: 'bg-amber-50 text-amber-600',
  calendar_overdue: 'bg-red-50 text-red-600',
  card_due_reminder: 'bg-red-50 text-red-600',
  card_expiring: 'bg-orange-50 text-orange-600',
  info: 'bg-sky-50 text-sky-600',
  warning: 'bg-amber-50 text-amber-600',
  success: 'bg-emerald-50 text-emerald-600',
};

// ── Notification Detail Modal (full-screen popup) ──────────────────────────────
interface NotifPopupProps {
  n: Notification;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

const NotifPopup = ({ n, onClose, onNavigate }: NotifPopupProps) => {
  const meta = getNotificationMeta(n.type);
  const typeLabel = TYPE_LABEL[n.type] ?? 'Notificação';
  const badgeColor = TYPE_BADGE_COLOR[n.type] ?? 'bg-gray-50 text-gray-600';

  const isCalendarFinancial = n.type === 'calendar_pending' || n.type === 'calendar_overdue';
  const isPaymentIssue = ['payment_rejected', 'payment_pending', 'upgrade_completed', 'plan_limit_reached'].includes(n.type);
  const isGoal = n.type === 'goal_completed';

  const calendarPath = () => {
    const date = n.data?.navigate_date as string | undefined;
    const sourceId = n.data?.source_event_id as string | undefined;
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (sourceId) params.set('open_event_id', sourceId);
    return `/calendario?${params.toString()}`;
  };

  // Full message body: for broadcasts look in data.body / data.content
  const broadcastContent = n.type === 'broadcast'
    ? ((n.data as Record<string, unknown>)?.body as string | undefined)
      ?? ((n.data as Record<string, unknown>)?.content as string | undefined)
    : undefined;

  // Financial detail info from notification data
  const txDescription = n.data?.description as string | undefined;
  const txAmount = n.data?.amount as number | undefined;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Colored header */}
        <div className={`px-6 pt-6 pb-5 ${meta.bg} flex items-start gap-4`}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/70">
            <i className={`${meta.icon} text-xl ${meta.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[11px] font-700 px-2 py-0.5 rounded-full ${badgeColor}`}>
                {typeLabel}
              </span>
              {!n.read_at && (
                <span className="w-2 h-2 bg-emerald-500 rounded-full" />
              )}
            </div>
            <h2 className="text-[15px] font-700 text-gray-900 leading-snug">{n.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-white/60 text-gray-500 cursor-pointer transition-colors flex-shrink-0"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {/* Financial extra info */}
          {isCalendarFinancial && (txDescription || txAmount !== undefined) && (
            <div className={`mb-4 p-3 rounded-xl flex items-center justify-between ${n.type === 'calendar_overdue' ? 'bg-red-50' : 'bg-amber-50'}`}>
              {txDescription && (
                <span className={`text-[13px] font-600 ${n.type === 'calendar_overdue' ? 'text-red-700' : 'text-amber-700'}`}>
                  {txDescription}
                </span>
              )}
              {txAmount !== undefined && (
                <span className={`text-[15px] font-700 ${n.type === 'calendar_overdue' ? 'text-red-700' : 'text-amber-700'}`}>
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(txAmount)}
                </span>
              )}
            </div>
          )}

          {/* Broadcast HTML content */}
          {broadcastContent ? (
            <div
              className="text-[14px] text-gray-700 leading-relaxed prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: broadcastContent }}
            />
          ) : n.body ? (
            <p className="text-[14px] text-gray-700 leading-relaxed">{n.body}</p>
          ) : (
            <p className="text-[14px] text-gray-400 italic">Sem detalhes adicionais.</p>
          )}

          {/* Timestamp */}
          <div className="mt-5 pt-4 border-t border-gray-100 flex items-center gap-1.5 text-[12px] text-gray-400">
            <i className="ri-time-line text-[13px]" />
            <span>{fmtRelative(n.created_at)} &bull; {fmtDatetime(n.created_at)}</span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="px-6 pb-5 flex flex-wrap items-center gap-2">
          {/* Calendar financial actions */}
          {isCalendarFinancial && (
            <>
              <button
                onClick={() => onNavigate(calendarPath())}
                className="flex-1 min-w-0 flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-700 text-white bg-emerald-500 hover:bg-emerald-600 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-checkbox-circle-line text-sm" />
                Pagar
              </button>
              <button
                onClick={() => onNavigate(calendarPath())}
                className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-600 text-amber-700 bg-amber-50 hover:bg-amber-100 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-time-line text-sm" />
                Adiar
              </button>
              <button
                onClick={() => onNavigate(calendarPath())}
                className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-600 text-gray-600 bg-gray-100 hover:bg-gray-200 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-calendar-line text-sm" />
                Ver no calendário
              </button>
            </>
          )}

          {/* Payment/plan issues */}
          {isPaymentIssue && (
            <button
              onClick={() => onNavigate('/perfil?tab=plano')}
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-700 text-white bg-emerald-500 hover:bg-emerald-600 cursor-pointer whitespace-nowrap transition-colors"
            >
              {n.type === 'plan_limit_reached' ? 'Fazer upgrade →' : 'Ver meu plano →'}
            </button>
          )}

          {/* Goal completed */}
          {isGoal && (
            <button
              onClick={() => onNavigate('/metas')}
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-700 text-white bg-emerald-500 hover:bg-emerald-600 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-trophy-line text-sm" />
              Ver minhas metas →
            </button>
          )}

          {/* Close button always present */}
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-[13px] font-600 text-gray-600 bg-gray-100 hover:bg-gray-200 cursor-pointer whitespace-nowrap transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

// ── AppHeader ─────────────────────────────────────────────────────────────────
export const AppHeader = ({ isCollapsed, onToggleSidebar, onOpenMobileSidebar, onSignOut, user }: AppHeaderProps) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [selectedNotif, setSelectedNotif] = useState<Notification | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { notifications, unreadCount, markAllRead, markRead } = useNotificationsContext();
  const { isPinEnabled, lock } = usePinLockContext();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setDropdownOpen(false);
    try {
      await onSignOut();
    } catch (err) {
      console.warn('[AppHeader] signOut error:', err);
    }
    // ProtectedRoute automatically redirects to '/' when user becomes null.
    // Calling navigate here before React processes the state update causes a
    // race condition where LandingPage still sees the authenticated user and
    // immediately redirects back to /dashboard.
  };

  const handleOpenNotif = () => {
    if (window.innerWidth < 768) {
      navigate('/notificacoes');
      return;
    }
    setNotifOpen(!notifOpen);
    setDropdownOpen(false);
  };

  // Clicking a notification item → open detail popup
  const handleNotifClick = (n: Notification) => {
    markRead(n.id);
    setNotifOpen(false);
    setSelectedNotif(n);
  };

  // Navigate from popup and close it
  const handlePopupNavigate = (path: string) => {
    setSelectedNotif(null);
    navigate(path);
  };

  return (
    <>
      <header
        className={`fixed top-0 right-0 h-[72px] bg-white border-b border-gray-100 z-30 flex items-center px-3 md:px-6 transition-all duration-300 left-0 ${
          isCollapsed ? 'lg:left-[68px]' : 'lg:left-[264px]'
        }`}
      >
        {/* Mobile hamburger */}
        <button
          onClick={onOpenMobileSidebar}
          className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600 mr-2 cursor-pointer shrink-0"
        >
          <i className="ri-menu-line text-xl" />
        </button>

        <div className="flex-1 min-w-0 max-w-xs md:max-w-md">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Buscar lançamento, conta..."
              className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-300 placeholder:text-gray-400 text-gray-700 transition-all hidden sm:block"
            />
          </div>
        </div>

        <div className="ml-auto flex items-center gap-1.5 shrink-0" ref={dropdownRef}>
          {/* Notifications bell */}
          <div className="relative">
            <button
              onClick={handleOpenNotif}
              className="relative w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-600 cursor-pointer transition-colors"
            >
              <i className="ri-notification-3-line text-[18px]" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[16px] h-4 flex items-center justify-center bg-red-500 rounded-full text-[9px] font-700 text-white px-0.5">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <div className="absolute right-0 top-12 w-[min(340px,calc(100vw-1rem))] bg-white rounded-2xl border border-gray-100 shadow-xl z-50 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-700 text-gray-900">Notificações</p>
                    {unreadCount > 0 && (
                      <span className="text-[10px] font-700 px-1.5 py-0.5 bg-red-100 text-red-600 rounded-full">
                        {unreadCount} nova{unreadCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllRead}
                      className="text-[11px] text-emerald-600 hover:text-emerald-700 font-600 cursor-pointer whitespace-nowrap"
                    >
                      Marcar todas
                    </button>
                  )}
                </div>

                {/* List */}
                <div className="max-h-[400px] overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="py-10 text-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <i className="ri-notification-off-line text-gray-400 text-lg" />
                      </div>
                      <p className="text-sm text-gray-500 font-500">Nenhuma notificação</p>
                      <p className="text-xs text-gray-400 mt-0.5">Tudo em ordem por aqui</p>
                    </div>
                  ) : (
                    notifications.slice(0, 8).map((n) => {
                      const meta = getNotificationMeta(n.type);
                      const isUnread = !n.read_at;
                      const typeLabel = TYPE_LABEL[n.type] ?? '';
                      const badgeColor = TYPE_BADGE_COLOR[n.type] ?? 'bg-gray-50 text-gray-600';
                      return (
                        <button
                          key={n.id}
                          onClick={() => handleNotifClick(n)}
                          className={`w-full text-left flex gap-3 px-4 py-3.5 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 cursor-pointer ${isUnread ? 'bg-emerald-50/40' : ''}`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${meta.bg}`}>
                            <i className={`${meta.icon} text-sm ${meta.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className={`text-[13px] leading-snug text-gray-900 ${isUnread ? 'font-700' : 'font-600'}`}>
                                {n.title}
                              </p>
                              {isUnread && (
                                <span className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0 mt-1.5" />
                              )}
                            </div>
                            {n.body && (
                              <p className="text-[12px] text-gray-500 mt-0.5 leading-snug line-clamp-2">{n.body}</p>
                            )}
                            <div className="flex items-center justify-between mt-1 gap-2">
                              <p className="text-[11px] text-gray-400">{timeAgo(n.created_at)}</p>
                              {typeLabel && (
                                <span className={`text-[10px] font-600 px-1.5 py-0.5 rounded-full ${badgeColor}`}>
                                  {typeLabel}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-100 px-4 py-2.5">
                  <button
                    onClick={() => { setNotifOpen(false); navigate('/notificacoes'); }}
                    className="w-full text-center text-[12px] font-600 text-emerald-600 hover:text-emerald-700 cursor-pointer py-1 transition-colors"
                  >
                    Ver central de notificações →
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* User Avatar */}
          <div className="relative">
            <button
              onClick={() => { setDropdownOpen(!dropdownOpen); setNotifOpen(false); }}
              className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-gray-100 cursor-pointer transition-colors"
            >
              {user.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt={user.name}
                  className="w-8 h-8 rounded-xl object-cover object-top shrink-0"
                />
              ) : (
                <div className="w-8 h-8 rounded-xl bg-emerald-500 flex items-center justify-center text-white text-[13px] font-700 shrink-0">
                  {user.avatarInitials}
                </div>
              )}
              <div className="hidden md:block text-left">
                <p className="text-[13px] font-600 text-gray-900 leading-none mb-0.5 max-w-[100px] truncate">{user.name}</p>
                <p className="text-[11px] text-gray-500 leading-none truncate max-w-[100px]">{user.planName}</p>
              </div>
              <i className="ri-arrow-down-s-line text-gray-400 text-sm hidden md:block" />
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 top-12 w-52 bg-white rounded-2xl border border-gray-100 shadow-lg py-2 z-50">
                <div className="px-4 py-2 border-b border-gray-100 mb-1">
                  <p className="text-[13px] font-600 text-gray-900">{user.fullName}</p>
                  <p className="text-[11px] text-gray-500 truncate">{user.planName}</p>
                </div>
                <button
                  onClick={() => { navigate('/perfil'); setDropdownOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <i className="ri-user-line text-gray-400 text-sm" />
                  Meu Perfil
                </button>
                <button
                  onClick={() => { navigate('/perfil'); setDropdownOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <i className="ri-settings-3-line text-gray-400 text-sm" />
                  Configurações
                </button>
                <div className="my-1 border-t border-gray-100" />
                {isPinEnabled && (
                  <button
                    onClick={() => { setDropdownOpen(false); lock(); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                  >
                    <i className="ri-lock-line text-sm" />
                    Bloquear agora
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                >
                  <i className="ri-logout-box-line text-sm" />
                  Sair
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Full-screen notification detail popup */}
      {selectedNotif && (
        <NotifPopup
          n={selectedNotif}
          onClose={() => setSelectedNotif(null)}
          onNavigate={handlePopupNavigate}
        />
      )}
    </>
  );
};
